import { END_POINTS } from '@config';
import { api } from '@core/baseApi';
import { HTTPResponse, LeadBranchGroupDetailsData } from '@types';

import {
  CreateMetaProviderRequest,
  GoogleAuthStatus,
  GoogleAuthUrlResponse,
  JobStatusResponse,
  LeadOrgConfig,
  MetaIntegrationProvider,
  MetaProviderResponse,
  UpdateMetaProviderRequest,
  UserOrgSettings
} from '../components/lead-settings/types';
import { TimelineResponse } from '../types/leadActivity';
import { DocumentItem } from '../types/leadFileUpload';
import { NoteResponse } from '../types/leadNote';
import {
  BranchMember,
  CreateLeadRequest,
  CreateTaskRequest,
  LeadAddToBranchRequest,
  LeadAddToGroupRequest
} from '../types/request';

import {
  Agent,
  AttachmentResponse,
  BulkUpdateStatusRequest,
  CallResponse,
  CreateCallResponse,
  CreateEventResponse,
  EventResponse,
  EventsListResponse,
  Lead,
  LeadCategory,
  LeadDetailsResponse,
  LeadListResponse,
  LocationsResponse,
  StatusEntity,
  StatusEntityResponse,
  Tag,
  TaskResponse,
  TasksListResponse
} from '../../../types/lead-management';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    // Upload management
    getPrivateFileUrl: builder.query<
      HTTPResponse<{ url: string }>,
      { documentId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_PRIVATE_FILE_URL(req.documentId),
        method: 'GET'
      })
    }),
    // Lead Management
    getLeads: builder.query<
      HTTPResponse<LeadListResponse>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEADS(req.organizationId)}${
          req.queryString || ''
        }`,
        method: 'GET'
      }),
      providesTags: ['LeadManagement']
    }),
    getStatusOptions: builder.query<
      HTTPResponse<StatusEntityResponse>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_STATUS(req.organizationId),
        method: 'GET'
      })
    }),
    getLeadDetails: builder.query<
      HTTPResponse<LeadDetailsResponse>,
      { organizationId: string; leadId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_DETAILS(
          req.organizationId,
          req.leadId
        ),
        method: 'GET'
      }),
      providesTags: ['LeadManagement']
    }),
    createLead: builder.mutation<
      HTTPResponse<Lead>,
      { organizationId: string; body: CreateLeadRequest }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_LEAD(req.organizationId),
        method: 'POST',
        body: req.body
      }),
      invalidatesTags: ['LeadManagement']
    }),

    updateLead: builder.mutation<
      HTTPResponse<Lead>,
      {
        organizationId: string;
        leadId: string;
        body: CreateLeadRequest;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_LEAD(
          req.organizationId,
          req.leadId
        ),
        method: 'PUT',
        body: req.body
      }),
      invalidatesTags: ['LeadManagement']
    }),

    deleteLead: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; leadId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_LEAD(
          req.organizationId,
          req.leadId
        ),
        method: 'DELETE'
      }),
      invalidatesTags: ['LeadManagement']
    }),
    downloadCsvTemplate: builder.query<
      HTTPResponse<{ object_key: string }>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DOWNLOAD_CSV_TEMPLATE(
          req.organizationId
        ),
        method: 'GET'
      })
    }),
    updateLeadAssignee: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; leadId: string; assignee: string | null }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_LEAD_ASSIGNEE(
          req.organizationId,
          req.leadId
        ),
        method: 'POST',
        body: { assignee_id: req.assignee }
      })
    }),
    leadBulkCreate: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; body: CreateLeadRequest[] }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_BULK_CREATE(req.organizationId),
        method: 'POST',
        body: req.body
      })
    }),
    // Assignee Management
    getAgents: builder.query<HTTPResponse<Agent[]>, { organizationId: string }>(
      {
        query: (req) => ({
          url: END_POINTS.LEAD_MANAGEMENT.GET_AGENTS(req.organizationId),
          method: 'GET'
        })
      }
    ),
    // Ads Management
    getAds: builder.query<
      HTTPResponse<{
        data: Array<{ id: string; name: string }>;
        pagination: any;
      }>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_ADS(req.organizationId)}${
          req.queryString || ''
        }`,
        method: 'GET'
      })
    }),
    // Category Management
    getCategories: builder.query<
      HTTPResponse<LeadCategory[]>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_CATEGORIES(req.organizationId),
        method: 'GET'
      })
    }),

    createCategory: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; name: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_CATEGORY(req.organizationId),
        method: 'POST',
        body: { value: req.name }
      })
    }),

    updateCategory: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; id: string; value: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_CATEGORY(
          req.organizationId,
          req.id
        ),
        method: 'PUT',
        body: { value: req.value }
      })
    }),

    deleteCategory: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; categoryId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_CATEGORY(
          req.organizationId,
          req.categoryId
        ),
        method: 'DELETE'
      })
    }),

    getStatus: builder.query<
      HTTPResponse<StatusEntity[]>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_STATUS(req.organizationId),
        method: 'GET'
      }),
      providesTags: ['LeadStatus']
    }),

    updateStatus: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; statuses: StatusEntity[] }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_STATUS(req.organizationId),
        method: 'POST',
        body: req.statuses
      }),
      invalidatesTags: ['LeadStatus']
    }),

    // Activity Management
    getLeadTasks: builder.query<
      TaskResponse,
      { organizationId: string; leadId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, leadId, page = 0, limit = 10 }) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEAD_TASKS(
          organizationId,
          leadId
        )}?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      providesTags: ['LeadTasks']
    }),

    createLeadTask: builder.mutation<
      HTTPResponse<{}>,
      { leadId: string; organizationId: string; task: CreateTaskRequest }
    >({
      query: ({ leadId, organizationId, task }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_LEAD_TASK(
          organizationId,
          leadId
        ),
        method: 'POST',
        body: task
      }),
      invalidatesTags: ['LeadTasks']
    }),
    deleteLeadTask: builder.mutation<
      HTTPResponse<{ status: string }>,
      { leadId: string; taskId: string; organizationId: string }
    >({
      query: ({ leadId, taskId, organizationId }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_LEAD_TASK(
          organizationId,
          leadId,
          taskId
        ),
        method: 'DELETE'
      }),
      invalidatesTags: ['LeadTasks']
    }),

    getLeadEvents: builder.query<
      EventResponse,
      { organizationId: string; leadId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, leadId, page = 0, limit = 10 }) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEAD_EVENTS(
          organizationId,
          leadId
        )}?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      providesTags: ['LeadEvents']
    }),
    createLeadEvent: builder.mutation<
      HTTPResponse<{ status: string }>,
      { leadId: string; organizationId: string; event: CreateEventResponse }
    >({
      query: ({ leadId, organizationId, event }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_LEAD_EVENT(
          organizationId,
          leadId
        ),
        method: 'POST',
        body: event
      }),
      invalidatesTags: ['LeadEvents']
    }),
    deleteLeadEvent: builder.mutation<
      HTTPResponse<{ status: string }>,
      { leadId: string; eventId: string; organizationId: string }
    >({
      query: ({ leadId, eventId, organizationId }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_LEAD_EVENT(
          organizationId,
          leadId,
          eventId
        ),
        method: 'DELETE'
      }),
      invalidatesTags: ['LeadEvents']
    }),
    getLeadCalls: builder.query<
      CallResponse,
      { organizationId: string; leadId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, leadId, page = 0, limit = 10 }) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEAD_CALLS(
          organizationId,
          leadId
        )}?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      providesTags: ['LeadCalls']
    }),
    createLeadCall: builder.mutation<
      HTTPResponse<{ status: string }>,
      { leadId: string; organizationId: string; call: CreateCallResponse }
    >({
      query: ({ leadId, organizationId, call }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_LEAD_CALL(
          organizationId,
          leadId
        ),
        method: 'POST',
        body: call
      }),
      invalidatesTags: ['LeadCalls']
    }),
    deleteLeadCall: builder.mutation<
      HTTPResponse<{ status: string }>,
      { leadId: string; callId: string; organizationId: string }
    >({
      query: ({ leadId, callId, organizationId }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_LEAD_CALL(
          organizationId,
          leadId,
          callId
        ),
        method: 'DELETE'
      }),
      invalidatesTags: ['LeadCalls']
    }),
    getLeadNotes: builder.query<
      NoteResponse,
      { organizationId: string; leadId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, leadId, page = 0, limit = 10 }) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEAD_NOTES(
          organizationId,
          leadId
        )}?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      providesTags: ['LeadNotes']
    }),
    getLeadAttachments: builder.query<
      AttachmentResponse[],
      { organizationId: string; leadId: string }
    >({
      query: ({ organizationId, leadId }) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_ATTACHMENTS(
          organizationId,
          leadId
        ),
        method: 'GET'
      })
    }),
    getLeadTimeline: builder.query<
      HTTPResponse<TimelineResponse>,
      { organizationId: string; leadId: string; page?: number; limit?: number }
    >({
      query: ({ organizationId, leadId, page = 0, limit = 10 }) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEAD_TIMELINE(
          organizationId,
          leadId
        )}?page=${page}&limit=${limit}`,
        method: 'GET'
      })
    }),

    // Tag Management
    getTags: builder.query<
      HTTPResponse<Tag[]>,
      { organizationId: string; searchTerm?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_TAGS(req.organizationId)}${
          req.searchTerm ? `?search-term=${req.searchTerm}` : ''
        }`,
        method: 'GET'
      })
    }),

    createTag: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; name: string; color: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_TAG(req.organizationId),
        method: 'POST',
        body: { name: req.name, color: req.color }
      })
    }),

    updateTag: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; id: string; name: string; color: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_TAG(req.organizationId, req.id),
        method: 'PUT',
        body: { name: req.name, color: req.color }
      })
    }),

    deleteTag: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; tagId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_TAG(
          req.organizationId,
          req.tagId
        ),
        method: 'DELETE'
      })
    }),

    leadBulkUpdate: builder.mutation<
      HTTPResponse<{ status: string }>,
      {
        leadIds: string[];
        tagIds?: string[];
        assignee?: string;
        status?: string;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_BULK_UPDATE,
        method: 'POST',
        body: {
          lead_ids: req.leadIds,
          tags: req.tagIds,
          assignee: req.assignee,
          status: req.status
        }
      })
    }),

    // Dashboard
    getLeadsDashboard: builder.query<
      HTTPResponse<{ status: string }>,
      { organisationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LEADS_DASHBOARD(
          req.organisationId
        )}${req.queryString || ''}`,
        method: 'GET'
      })
    }),

    getAgentsStatusSummary: builder.query<
      HTTPResponse<{ status: string }>,
      { organisationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_AGENTS_STATUS_SUMMARY(
          req.organisationId
        )}${req.queryString || ''}`,
        method: 'GET'
      })
    }),
    getGoogleAuthStatus: builder.query<
      HTTPResponse<GoogleAuthStatus>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_GOOGLE_STATUS(req.organizationId),
        method: 'GET'
      })
    }),
    getGoogleAuthUrl: builder.query<
      HTTPResponse<GoogleAuthUrlResponse>,
      { organizationId: string; redirectUrl: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_GOOGLE_AUTH_URL(
          req.organizationId,
          req.redirectUrl
        ),
        method: 'GET'
      })
    }),
    getUserOrgSettings: builder.query<
      HTTPResponse<UserOrgSettings>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_GOOGLE_USER_ORG_SETTINGS(
          req.organizationId
        ),
        method: 'GET'
      })
    }),
    updateUserOrgSettings: builder.mutation<
      HTTPResponse<UserOrgSettings>,
      {
        organizationId: string;
        body: UserOrgSettings;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_GOOGLE_USER_ORG_SETTINGS(
          req.organizationId
        ),
        method: 'PUT',
        body: req.body
      })
    }),
    revokeGoogleAuth: builder.mutation<
      HTTPResponse<{ message: string }>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_GOOGLE_AUTH(req.organizationId),
        method: 'DELETE'
      })
    }),
    getUploadUrl: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; fileType: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPLOAD_FILE(
          req.organizationId,
          req.fileType
        ),
        method: 'GET'
      })
    }),
    getFileUrl: builder.query<
      HTTPResponse<{ url: string }>,
      { organizationId: string; documentId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_FILE_URL(
          req.organizationId,
          req.documentId
        ),
        method: 'GET'
      })
    }),
    createLeadNote: builder.mutation<
      HTTPResponse<{ status: string }>,
      {
        organizationId: string;
        leadId: string;
        payload: {
          note: string;
          documents: DocumentItem[];
        };
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_LEAD_NOTE(
          req.organizationId,
          req.leadId
        ),
        method: 'POST',
        body: req.payload
      })
    }),
    deleteLeadNote: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; leadId: string; noteId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.DELETE_LEAD_NOTE(
          req.organizationId,
          req.leadId,
          req.noteId
        ),
        method: 'DELETE'
      })
    }),
    updateLeadNote: builder.mutation<
      HTTPResponse<{ status: string }>,
      {
        organizationId: string;
        leadId: string;
        payload: {
          id: string;
          note: string;
          documents_to_add: DocumentItem[];
          documents_to_delete: string[];
        };
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_LEAD_NOTE(
          req.organizationId,
          req.leadId,
          req.payload.id
        ),
        method: 'PUT',
        body: req.payload
      })
    }),
    // Location Management
    getLocations: builder.query<
      HTTPResponse<LocationsResponse>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_LOCATIONS(req.organizationId)}${
          req.queryString || ''
        }`,
        method: 'GET'
      })
    }),
    getCountByStatusId: builder.query<
      HTTPResponse<any>,
      { organizationId: string; statusId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_COUNT_BY_STATUS_ID(
          req.organizationId,
          req.statusId
        ),
        method: 'GET'
      })
    }),
    bulkUpdateStatus: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; body: BulkUpdateStatusRequest }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.BULK_UPDATE_STATUS(req.organizationId),
        method: 'PATCH',
        body: req.body
      })
    }),
    leadAddToBranchGroupDetails: builder.mutation<
      HTTPResponse<LeadBranchGroupDetailsData[]>,
      { organizationId: string; body: BranchMember }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_ADD_TO_BRANCH_GROUP_DETAILS(
          req.organizationId
        ),
        method: 'POST',
        body: req.body
      })
    }),
    leadAddToBranch: builder.mutation<
      HTTPResponse<{ status: string }>,
      LeadAddToBranchRequest
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_ADD_TO_BRANCH(req.params),
        method: 'POST',
        body: req.body
      })
    }),
    leadAddToGroup: builder.mutation<
      HTTPResponse<{ status: string }>,
      LeadAddToGroupRequest
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_ADD_TO_GROUP(req.params),
        method: 'POST',
        body: req.body
      })
    }),
    leadEditMember: builder.mutation<
      HTTPResponse<{ status: string }>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        groupId: string;
        memberId: string;
        body: any;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_EDIT_MEMBER(
          req.organizationId,
          req.leadId,
          req.branchId,
          req.groupId,
          req.memberId
        ),
        method: 'POST',
        body: req.body
      })
    }),
    leadMemberFeeCollectionSettings: builder.query<
      HTTPResponse<any>,
      {
        organizationId: string;
        branchId: string;
        groupId: string;
        memberId: string;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_MEMBER_FEE_COLLECTION_SETTINGS(
          req.organizationId,
          req.branchId,
          req.groupId,
          req.memberId
        ),
        method: 'GET'
      })
    }),
    leadBranchGroupDetails: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; body: BranchMember[] }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_BRANCH_GROUP_DETAILS(
          req.organizationId
        ),
        method: 'POST',
        body: req.body
      })
    }),
    leadQuickCollect: builder.mutation<
      HTTPResponse<any>,
      { organizationId: string; leadId: string; branchId: string; body: any }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_QUICK_COLLECT(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId
        ),
        method: 'POST',
        body: reqBody.body
      })
    }),
    leadMarkAsPaid: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_MARK_AS_PAID(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId,
          reqBody.paymentOrderId
        ),
        method: 'POST',
        body: reqBody.body
      })
    }),
    leadSendPaymentReminder: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_SEND_PAYMENT_REMINDER(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId,
          reqBody.paymentOrderId
        ),
        method: 'POST',
        body: reqBody.body
      })
    }),
    leadDisablePaymentOrder: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_DISABLE_PAYMENT_ORDER(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId,
          reqBody.paymentOrderId
        ),
        method: 'PATCH',
        body: reqBody.body
      })
    }),
    enableSplitPayment: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.ENABLE_SPLIT_PAYMENT(
          req.organizationId,
          req.leadId,
          req.branchId,
          req.paymentOrderId
        ),
        method: 'PATCH'
      })
    }),
    getLeadPaymentOrderDetails: builder.query<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_PAYMENT_ORDER_DETAILS(
          req.organizationId,
          req.leadId,
          req.branchId,
          req.paymentOrderId
        ),
        method: 'GET'
      })
    }),
    getLeadPaymentOrderHistory: builder.query<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_PAYMENT_ORDER_HISTORY(
          req.organizationId,
          req.leadId,
          req.branchId,
          req.paymentOrderId
        ),
        method: 'GET'
      })
    }),
    leadDiscount: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_DISCOUNT(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId,
          reqBody.paymentOrderId
        ),
        method: 'PATCH',
        body: reqBody.body
      })
    }),
    leadDownloadPaymentReceipt: builder.query<
      HTTPResponse<any>,
      { organizationId: string; leadId: string; paymentOrderId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_DOWNLOAD_PAYMENT_RECEIPT(
          req.organizationId,
          req.leadId,
          req.paymentOrderId
        ),
        method: 'GET'
      })
    }),
    getLeadPaymentOrders: builder.query<
      HTTPResponse<any>,
      { organizationId: string; leadId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_PAYMENT_ORDERS(
          req.organizationId,
          req.leadId
        ),
        method: 'GET'
      })
    }),
    leadClosePayment: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        leadId: string;
        branchId: string;
        paymentOrderId: string;
        body: any;
      }
    >({
      query: (reqBody) => ({
        url: END_POINTS.LEAD_MANAGEMENT.LEAD_CLOSE_PAYMENT(
          reqBody.organizationId,
          reqBody.leadId,
          reqBody.branchId,
          reqBody.paymentOrderId
        ),
        method: 'PATCH',
        body: reqBody.body
      })
    }),
    // Activities Management - All Tasks across leads
    getAllTasks: builder.query<
      HTTPResponse<TasksListResponse>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_ALL_TASKS(req.organizationId)}${
          req.queryString || ''
        }`,
        method: 'GET'
      })
    }),
    // Activities Management - All Events across leads
    getAllEvents: builder.query<
      HTTPResponse<EventsListResponse>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.LEAD_MANAGEMENT.GET_ALL_EVENTS(req.organizationId)}${
          req.queryString || ''
        }`,
        method: 'GET'
      })
    }),
    // Org LMS config (lead assignment rules, etc.)
    getLeadOrgConfig: builder.query<
      HTTPResponse<LeadOrgConfig>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_LEAD_ORG_CONFIG(req.organizationId),
        method: 'GET'
      }),
      providesTags: ['LeadOrgConfig']
    }),
    updateLeadOrgConfig: builder.mutation<
      HTTPResponse<LeadOrgConfig>,
      { organizationId: string; body: Partial<LeadOrgConfig> }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_LEAD_ORG_CONFIG(
          req.organizationId
        ),
        method: 'PUT',
        body: req.body
      }),
      invalidatesTags: ['LeadOrgConfig']
    }),
    // Meta Integration
    getMetaProvider: builder.query<
      HTTPResponse<MetaIntegrationProvider>,
      { organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_META_PROVIDER(req.organizationId),
        method: 'GET'
      }),
      providesTags: ['MetaProvider']
    }),
    createMetaProvider: builder.mutation<
      HTTPResponse<MetaProviderResponse>,
      { organizationId: string; body: CreateMetaProviderRequest }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.CREATE_META_PROVIDER(
          req.organizationId
        ),
        method: 'POST',
        body: req.body
      }),
      invalidatesTags: ['MetaProvider']
    }),
    updateMetaProvider: builder.mutation<
      HTTPResponse<MetaProviderResponse>,
      {
        organizationId: string;
        providerId: string;
        body: UpdateMetaProviderRequest;
      }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.UPDATE_META_PROVIDER(
          req.organizationId,
          req.providerId
        ),
        method: 'PATCH',
        body: req.body
      }),
      invalidatesTags: ['MetaProvider']
    }),
    getJobStatus: builder.query<
      HTTPResponse<JobStatusResponse>,
      { organizationId: string; jobId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_JOB_STATUS(
          req.organizationId,
          req.jobId
        ),
        method: 'GET'
      })
    })
  })
});

export const {
  // Upload management
  useGetPrivateFileUrlQuery,
  useLazyGetPrivateFileUrlQuery,

  // Lead Management
  useGetLeadsQuery,
  useLazyGetLeadsQuery,
  useLazyGetLeadDetailsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useUpdateLeadAssigneeMutation,
  useLeadBulkCreateMutation,

  // Agent Management
  useGetAgentsQuery,
  useLazyGetAgentsQuery,

  // Ads Management
  useGetAdsQuery,
  useLazyGetAdsQuery,

  useDownloadCsvTemplateQuery,
  useLazyDownloadCsvTemplateQuery,
  useLeadBulkUpdateMutation,
  useGetLeadDetailsQuery,

  // Category Management
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,

  // Status Management
  useUpdateStatusMutation,
  useGetStatusOptionsQuery,
  useGetStatusQuery,
  useLazyGetStatusQuery,

  // Task Management
  useGetLeadTasksQuery,
  useLazyGetLeadTasksQuery,
  useCreateLeadTaskMutation,
  useDeleteLeadTaskMutation,

  // Event Management
  useGetLeadEventsQuery,
  useLazyGetLeadEventsQuery,
  useCreateLeadEventMutation,
  useDeleteLeadEventMutation,

  // Call Management
  useGetLeadCallsQuery,
  useLazyGetLeadCallsQuery,
  useCreateLeadCallMutation,
  useDeleteLeadCallMutation,

  // Tag Management
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,

  // Dashboard
  useGetLeadsDashboardQuery,
  useLazyGetLeadsDashboardQuery,
  useGetAgentsStatusSummaryQuery,
  useLazyGetAgentsStatusSummaryQuery,

  // Google Calendar Integration
  useGetGoogleAuthStatusQuery,
  useLazyGetGoogleAuthStatusQuery,
  useGetGoogleAuthUrlQuery,
  useLazyGetGoogleAuthUrlQuery,
  useGetUserOrgSettingsQuery,
  useLazyGetUserOrgSettingsQuery,
  useUpdateUserOrgSettingsMutation,
  useRevokeGoogleAuthMutation,
  // Note Management
  useGetLeadNotesQuery,
  useLazyGetLeadNotesQuery,
  useCreateLeadNoteMutation,
  useDeleteLeadNoteMutation,
  useUpdateLeadNoteMutation,

  // Attachment Management
  useGetLeadAttachmentsQuery,
  useLazyGetLeadAttachmentsQuery,

  // Timeline Management
  useGetLeadTimelineQuery,
  useLazyGetLeadTimelineQuery,

  // File Management
  useGetUploadUrlMutation,
  useGetFileUrlQuery,
  useLazyGetFileUrlQuery,

  // Location Management
  useGetLocationsQuery,
  useLazyGetLocationsQuery,

  // Status Management
  useLazyGetCountByStatusIdQuery,
  useBulkUpdateStatusMutation,

  // Lead Management
  useLeadAddToBranchMutation,
  useLeadAddToGroupMutation,
  useLeadEditMemberMutation,
  useLazyLeadMemberFeeCollectionSettingsQuery,
  useLeadBranchGroupDetailsMutation,
  useLeadAddToBranchGroupDetailsMutation,
  // Payment Management
  useLeadQuickCollectMutation,
  useLeadMarkAsPaidMutation,
  useLeadSendPaymentReminderMutation,
  useLeadDisablePaymentOrderMutation,
  useLeadDiscountMutation,
  useLazyGetLeadPaymentOrdersQuery,
  useLazyGetLeadPaymentOrderDetailsQuery,
  useLazyGetLeadPaymentOrderHistoryQuery,
  useLeadDownloadPaymentReceiptQuery,
  useLazyLeadDownloadPaymentReceiptQuery,
  useEnableSplitPaymentMutation,
  useLeadClosePaymentMutation,
  // Activities Management
  useGetAllTasksQuery,
  useLazyGetAllTasksQuery,
  useGetAllEventsQuery,
  useLazyGetAllEventsQuery,
  // LMS config
  useGetLeadOrgConfigQuery,
  useUpdateLeadOrgConfigMutation,
  // Meta Integration
  useGetMetaProviderQuery,
  useLazyGetMetaProviderQuery,
  useCreateMetaProviderMutation,
  useUpdateMetaProviderMutation,
  useLazyGetJobStatusQuery
} = extendedBaseQuery;
